import { TextField } from '@material-ui/core';
import CSS from 'csstype';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { useFormBuilderStore } from '~/src/dash/components/forms/formBuilder/contexts';
import { IDaypartingPeriod } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/common';
import { DaypartingDayToggles } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder/DaypartingDayToggles';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  Div,
  fontSizing,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface IDaypartingPeriodFormProps {
  index: number;
  handleRemovePeriod: (idx: number) => void;
  isRemoveDisabled: boolean;
  key: string | number;
  period: IDaypartingPeriod;
}

const DaypartingPeriodForm = ({
  handleRemovePeriod,
  index,
  isRemoveDisabled,
  period,
}: IDaypartingPeriodFormProps) => {
  const { start, end, days } = period;

  const [startTime, setStartTime] = useState(start);
  const [endTime, setEndTime] = useState(end);
  const [activeDays, setActiveDays] = useState<Set<string>>(days || new Set([]));
  const [timeError, setTimeError] = useState('');

  const handleTimeChange = (time: string, point: 'start' | 'end') => {
    const newMoment = moment(time, 'HH:mm');
    if (point === 'start') {
      if (endTime.isBefore(newMoment)) {
        setTimeError('Start time must be before end time.');
      } else {
        unstable_batchedUpdates(() => {
          setStartTime(newMoment);
          setTimeError('');
        });
        period.start = newMoment;
      }
    } else {
      if (startTime.isAfter(newMoment)) {
        setTimeError('End time must be after start time.');
      } else {
        unstable_batchedUpdates(() => {
          setEndTime(newMoment);
          setTimeError('');
        });
        period.end = newMoment;
      }
    }
  };

  const handleToggleDay = (value: string) => {
    const clonedActiveDays = cloneDeep(activeDays);
    if (activeDays.has(value)) {
      clonedActiveDays.delete(value);
    } else {
      clonedActiveDays.add(value);
    }
    setActiveDays(clonedActiveDays);
    period.days = clonedActiveDays;
  };

  return (
    <Rows
      gap={spacing.small}
      smallVerticalPadding
      horizontalPadding
      style={{
        border: `1px solid ${publicaColors.lightGray}`,
        borderRadius: borderRadius.base,
        backgroundColor: publicaColors.white,
      }}
    >
      <Row noPadding>
        <Cols gap={spacing.large}>
          {/* START */}
          <Col>
            <Rows>
              <Row noPadding>
                <TextField
                  id='time-picker-start'
                  type='time'
                  label='Start'
                  value={startTime.format('HH:mm')}
                  onChange={(e) => handleTimeChange(e.target.value, 'start')}
                  InputProps={{
                    style: timeInputStyle,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ marginTop: 0 }}
                />
              </Row>
            </Rows>
          </Col>
          {/* END */}
          <Col>
            <Rows>
              <Row noPadding>
                <TextField
                  id='time-picker-end'
                  type='time'
                  label='End'
                  value={endTime.format('HH:mm')}
                  onChange={(e) => handleTimeChange(e.target.value, 'end')}
                  InputProps={{
                    style: timeInputStyle,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  style={{ marginTop: 0 }}
                />
              </Row>
            </Rows>
          </Col>
          <Col flex style={{ textAlign: 'right' }}>
            <Div
              onClick={() => !isRemoveDisabled && handleRemovePeriod(index)}
              style={{ cursor: isRemoveDisabled ? 'default' : 'pointer' }}
            >
              <IconV2
                name='close'
                color={isRemoveDisabled ? publicaColors.lightGray : publicaColors.veryDarkGray}
              />
            </Div>
          </Col>
        </Cols>
      </Row>
      {timeError && (
        <Row noPadding>
          <Text extraSmall color={publicaColors.red}>
            {timeError}
          </Text>
        </Row>
      )}
      <Row noPadding>
        <Text small>Repeating On</Text>
      </Row>
      <Row noPadding>
        <DaypartingDayToggles selected={activeDays} onClick={handleToggleDay} />
      </Row>
    </Rows>
  );
};

const timeInputStyle: CSS.Properties = {
  fontSize: fontSizing.small,
  width: '108px',
};

interface IProps {
  initialPeriods: IDaypartingPeriod[];
}

export const DaypartingScheduleBuilder = ({ initialPeriods }: IProps) => {
  const { object } = useFormBuilderStore();

  const isMounted = useRef(false);
  const [periods, setPeriods] = useState(initialPeriods);
  const [counter, setCounter] = useState(1);

  useEffect(() => {
    if (isMounted.current) {
      setPeriods(initialPeriods);
    } else {
      isMounted.current = true;
    }
  }, [initialPeriods]);

  const handleAddPeriod = () => {
    unstable_batchedUpdates(() => {
      setPeriods((prevPeriods) => {
        const clonedPeriods = cloneDeep(prevPeriods);
        clonedPeriods.push({
          reactKey: counter,
          start: moment('00:00', 'HH:mm'),
          end: moment('12:00', 'HH:mm'),
          days: new Set([]),
        });
        object.DaypartingUI.Periods = clonedPeriods;
        return clonedPeriods;
      });
      setCounter(counter + 1);
    });
  };

  const handleRemovePeriod = (idx: number) => {
    setPeriods((prevPeriods) => {
      const clonedPeriods = cloneDeep(prevPeriods);
      clonedPeriods.splice(idx, 1);
      object.DaypartingUI.Periods = clonedPeriods;
      return clonedPeriods;
    });
  };

  return (
    <Rows gap={spacing.base}>
      {periods.map((period, i) => {
        const isRemoveDisabled = periods.length === 1 && i === 0;
        return (
          <Row noPadding key={period.reactKey}>
            <DaypartingPeriodForm
              period={period}
              key={period.reactKey}
              handleRemovePeriod={handleRemovePeriod}
              index={i}
              isRemoveDisabled={isRemoveDisabled}
            />
          </Row>
        );
      })}
      <Row noPadding>
        <TextButton size='small' onClick={handleAddPeriod} disabled={periods.length >= 20}>
          + Add Time Period
        </TextButton>
      </Row>
    </Rows>
  );
};
